import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getWorkOrders, approveWorkOrder } from '../services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  CheckCircle2,
  XCircle,
  FileText,
  Calendar,
  DollarSign,
  User,
  Loader2,
} from 'lucide-react';

const ApprovalsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadWorkOrders();
  }, []);

  useEffect(() => {
    const workOrderId = searchParams.get('work_order');
    if (workOrderId && workOrders.length > 0) {
      const order = workOrders.find(wo => wo.id === workOrderId);
      if (order) {
        setSelectedOrder(order);
      }
    }
  }, [searchParams, workOrders]);

  const loadWorkOrders = async () => {
    try {
      const data = await getWorkOrders({ status: 'pending_approval' });
      setWorkOrders(data);
    } catch (error) {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (approved) => {
    if (!approved && !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setIsApproving(true);
    try {
      await approveWorkOrder(selectedOrder.id, approved, !approved ? rejectionReason : null);
      toast.success(approved ? 'Work order approved successfully' : 'Work order rejected');
      setSelectedOrder(null);
      setRejectionReason('');
      loadWorkOrders();
    } catch (error) {
      toast.error('Failed to process approval');
    } finally {
      setIsApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="approvals-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold font-heading text-foreground">Approval Queue</h1>
        <p className="text-muted-foreground mt-2">
          Review and approve work orders pending approval
        </p>
      </div>

      {/* Work Orders Grid */}
      {workOrders.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {workOrders.map((order) => (
            <Card
              key={order.id}
              className="work-order-card cursor-pointer"
              onClick={() => setSelectedOrder(order)}
              data-testid={`approval-card-${order.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold font-heading">{order.title}</h3>
                      <Badge className="bg-amber-100 text-amber-800">Pending Approval</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {order.description}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {order.contractor_name || 'Unassigned'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Created: {format(new Date(order.created_at), 'MMM d, yyyy')}
                      </span>
                      {order.estimated_cost && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4" />
                          ${order.estimated_cost.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button size="sm" data-testid={`review-button-${order.id}`}>
                    Review
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12">
            <div className="text-center text-muted-foreground">
              <CheckCircle2 className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg mb-2">All caught up!</p>
              <p className="text-sm">No work orders pending approval</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approval Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Review Work Order</DialogTitle>
            <DialogDescription>Review details and provide your digital signature</DialogDescription>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6">
              {/* Work Order Details */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold font-heading mb-2">{selectedOrder.title}</h3>
                  <p className="text-sm text-muted-foreground">{selectedOrder.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Work Type</p>
                    <p className="text-sm font-medium capitalize">
                      {selectedOrder.work_type.replace('_', ' ')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Priority</p>
                    <p className="text-sm font-medium capitalize">{selectedOrder.priority}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Section Code</p>
                    <p className="text-sm font-medium">{selectedOrder.section_code}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Contractor</p>
                    <p className="text-sm font-medium">{selectedOrder.contractor_name || 'Unassigned'}</p>
                  </div>
                  {selectedOrder.deadline && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Deadline</p>
                      <p className="text-sm font-medium">
                        {format(new Date(selectedOrder.deadline), 'MMM d, yyyy')}
                      </p>
                    </div>
                  )}
                  {selectedOrder.estimated_cost && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Estimated Cost</p>
                      <p className="text-sm font-medium">
                        ${selectedOrder.estimated_cost.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Photos */}
              {selectedOrder.photos && selectedOrder.photos.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-3">Photos ({selectedOrder.photos.length})</h4>
                  <div className="grid grid-cols-4 gap-2">
                    {selectedOrder.photos.map((photo, index) => (
                      <img
                        key={index}
                        src={photo}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Rejection Reason */}
              <div className="space-y-2">
                <Label htmlFor="rejection-reason">Rejection Reason (Optional - required if rejecting)</Label>
                <Textarea
                  id="rejection-reason"
                  data-testid="rejection-reason-input"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Provide a reason if rejecting this work order..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setSelectedOrder(null);
                setRejectionReason('');
              }}
              data-testid="cancel-approval-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleApprove(false)}
              disabled={isApproving}
              data-testid="reject-button"
            >
              {isApproving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <XCircle className="mr-2 h-4 w-4" />
              )}
              Reject
            </Button>
            <Button
              onClick={() => handleApprove(true)}
              disabled={isApproving}
              data-testid="approve-button"
            >
              {isApproving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalsPage;
